import { useEffect, useState } from 'react';

import type { AdvisorDashboardSuccess } from 'components/GlassCards/types';

import APIHandler from 'config/APIHandler';
import { storageKeys } from 'config/settings.json';
import StorageHandler from 'config/StorageHandler';

import i18n from 'i18n-react';

function parseAdvisorResponse(res: unknown): { data: AdvisorDashboardSuccess | null; error: string | null } {
  if (res === false || res === null) {
    return { data: null, error: i18n.translate('glassWidgets.errors.requestFailed') as string };
  }
  if (typeof res !== 'object') {
    return { data: null, error: 'Unexpected response' };
  }
  const payload = res as { result?: string; response?: unknown; message?: unknown };

  if (payload.result === 'success') {
    return { data: res as AdvisorDashboardSuccess, error: null };
  }

  if (typeof payload.response === 'string') {
    return { data: null, error: payload.response };
  }
  if (typeof payload.message === 'string') {
    return { data: null, error: payload.message };
  }
  return { data: null, error: 'Request failed' };
}

export interface UseAdvisorDashboardDataResult {
  data: AdvisorDashboardSuccess | null;
  error: string | null;
  isLoading: boolean;
}

/**
 * Loads advisor dashboard payload via APIHandler.getAdvisorDashboardData (single request for glass widgets).
 */
export function useAdvisorDashboardData(
  appId: number | null | undefined,
  langCode = 'en-US',
): UseAdvisorDashboardDataResult {
  const [data, setData] = useState<AdvisorDashboardSuccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (appId === null || (appId && appId < 0)) {
      setData(null);
      setError(null);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const fetchAdvisorDashboardData = async () => {
      // TODO: Confirm implementation below meets requirements.
      /**
       * Checks for a user exists AND a token does not exists because the StorageHandler.exists() returns a falsy value if the token is not both present AND valid.
       * The redirect also checks for an existing user, because the homepage is currently a public page and should not be locked behind a login page,
       * but there is no refresh mechanic for the accesstoken specifically. Should that ever be implemented, replace this
       * section with one that checks the access token validity and refreshes it if necessary.
       */
      const userExists = (await StorageHandler.exists(storageKeys.user)) as boolean;
      const accessTokenExists = (await StorageHandler.exists(storageKeys.accessToken)) as boolean;
      if (userExists && !accessTokenExists) {
        window.location.assign('/logout');
        return;
      }

      const accessToken = (await StorageHandler.load(storageKeys.accessToken)) as string | null | undefined;

      if (cancelled) return;
      if (!accessToken || StorageHandler.isExpired(storageKeys.accessToken)) {
        setData(null);
        setError(null);
        setIsLoading(false);
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- APIHandler typings
      const raw = await APIHandler.getAdvisorDashboardData(appId, accessToken, langCode);
      if (cancelled) return;
      const { data: next, error: err } = parseAdvisorResponse(raw);
      setData(next);
      setError(err);
      setIsLoading(false);
    };

    void fetchAdvisorDashboardData();

    return () => {
      cancelled = true;
    };
  }, [appId, langCode]);

  return { data, error, isLoading };
}
